/**
 * Frontend customer-status helpers.
 *
 * Pure functions used to decide UI state from the customer record returned
 * by the portal API. Extracted so the same logic can be tested in isolation,
 * outside the React render path.
 *
 * Mirror of the backend's customer-status.mjs but tailored to UI concerns
 * (which onboarding step to show, whether onboarding is finished, etc.).
 * Keep this file in sync with `portal/backend/lib/customer-status.mjs`.
 */

export const STATUS = Object.freeze({
  REGISTERED: 'registered',
  CONSENT_PENDING: 'consent-pending',
  CONSENT_CONFIRMED: 'consent-confirmed',
  DEPLOYING: 'deploying',
  DEPLOYED: 'deployed',
  ACTIVE: 'active',
  DEPLOY_FAILED: 'deploy-failed',
  PENDING_DECOMMISSION: 'pending-decommission',
  DECOMMISSIONED: 'decommissioned',
  SUSPENDED_TRUST_LOST: 'suspended-trust-lost',
});

/**
 * Statuses that mean "the customer should be on the onboarding wizard,
 * not the main dashboard". Used by Dashboard.jsx to redirect.
 */
const PRE_DEPLOY_STATUSES = new Set([
  STATUS.REGISTERED,
  STATUS.CONSENT_PENDING,
  STATUS.CONSENT_CONFIRMED,
  STATUS.DEPLOYING,
]);

/**
 * True when the customer hasn't completed onboarding yet.
 * Treats "no status set" as needing onboarding (defensive).
 */
export function needsOnboarding(customer) {
  const status = customer?.status;
  if (!status) return true;
  return PRE_DEPLOY_STATUSES.has(status);
}

/**
 * Which onboarding step should we show given the customer's current state?
 *
 * Returns one of: 'consent' | 'trust' | 'deploy' | 'done'.
 *
 * The decision tree is intentionally explicit and ordered most-advanced
 * first, same shape as the original useEffect in Onboarding.jsx.
 *
 * Regression note: this is exactly the logic that broke the consent link.
 * The "consent-confirmed" gate is critical: a customer with `entraTenantId`
 * set but `status === 'registered'` MUST stay on the consent step so they
 * can see the admin-consent URL. Don't loosen the check without updating
 * the tests in customer-status.test.js.
 */
export function detectActiveStep(customer) {
  if (!customer) return 'consent';
  // An operator sent this customer back to the admin-consent sign-in
  // (ENTRAID-261): Step 1 first, whatever the status; the stack is kept.
  if (customer.entraConsentResetAt) return 'consent';

  if (customer.status === STATUS.DEPLOYED || customer.status === STATUS.ACTIVE) {
    return 'done';
  }
  if (customer.status === STATUS.DEPLOYING) {
    return 'deploy';
  }
  // A trust role recorded before consent is confirmed (the trust stack can
  // now register itself, ENTRAID-260) must not skip Step 1: deploying needs
  // consent-confirmed (or a retry after deploy-failed).
  if (customer.trustRoleArn
      && customer.status !== STATUS.REGISTERED
      && customer.status !== STATUS.CONSENT_PENDING) {
    return 'deploy';
  }
  if (customer.entraTenantId && customer.status === STATUS.CONSENT_CONFIRMED) {
    return 'trust';
  }
  return 'consent';
}

/**
 * True when the customer's collector stack has been provisioned in their
 * AWS account (the portal can call cross-account APIs against it).
 */
export function isDeployed(customer) {
  return customer?.status === STATUS.DEPLOYED || customer?.status === STATUS.ACTIVE;
}

/**
 * Microsoft admin consent as the Deployment section shows it (portal
 * workbench). An operator reset (ENTRAID-261, entraConsentResetAt) wins
 * over the status: the customer is back at Step 1 until consent is granted
 * again.
 */
export const CONSENT_STATE = Object.freeze({
  NOT_STARTED: 'not-started',
  PENDING: 'pending',
  GRANTED: 'granted',
  RESET: 'reset',
});

export const CONSENT_STATE_LABELS = Object.freeze({
  [CONSENT_STATE.NOT_STARTED]: 'Not started',
  [CONSENT_STATE.PENDING]: 'Waiting for Global Admin consent',
  [CONSENT_STATE.GRANTED]: 'Granted',
  [CONSENT_STATE.RESET]: 'Reset by IPC Global, waiting for consent again',
});

export function consentState(customer) {
  if (!customer) return CONSENT_STATE.NOT_STARTED;
  if (customer.entraConsentResetAt) return CONSENT_STATE.RESET;
  if (!customer.status || customer.status === STATUS.REGISTERED) {
    return customer.entraTenantId ? CONSENT_STATE.PENDING : CONSENT_STATE.NOT_STARTED;
  }
  if (customer.status === STATUS.CONSENT_PENDING) return CONSENT_STATE.PENDING;
  return CONSENT_STATE.GRANTED;
}
